import React, { FC, memo } from 'react';
import styled, { css } from 'styled-components';
import ProfileView from '@blocs.profile-view';
import { AppType } from '@blocs.common';
import { gqlCountryEnum as COUNTRY } from '@minecraft.graphql-types';
import { media } from '@minecraft.themes';
import ImpactedServiceAlert from '@blocs.impacted-service-alert';
import { PageWrapperStyles } from '../../../shared/styles';
import { Wrapper } from '../../shared/styles';
import RecipientHeader from '../../common/RecipientHeader';
import RecipientFooter from '../../common/RecipientFooter';
import RecipientViewExpired from '../../common/RecipientViewExpired';

const hideOnSm = css`
  display: none;

  ${media.sm`
    display: flex;
  `}
`;

const ProfileViewWrapper = styled.div`
  padding-top: 1rem;
  padding-bottom: 1rem;

  & > div {
    ${PageWrapperStyles}
    border: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const StyledHeader = styled(RecipientHeader)`
  ${hideOnSm}
  min-height: auto;
`;

const StyledFooter = styled(RecipientFooter)`
  ${hideOnSm}
`;

type TalentInfoProps = {
  isShareSelectsPresentation: boolean;
  showSummaryWithFolderCustomization: boolean;
  currentTalentGuid: string;
  roleId?: number;
  folderId?: number;
  accountCountryCode?: COUNTRY;
  userName?: string;
  userNameModalToggle?: () => void;
  presentationGuid?: string;
  presentationId?: number;
  pickedBy?: { id: string; useName: number }[];
  pickCount?: number;
};

type RecipientDetailViewMainContentProps = {
  isShareSelectsPresentation: boolean;
  showSummaryWithFolderCustomization: boolean;
  darkTheme: boolean;
  roleId?: number;
  folderId?: number;
  expired?: boolean;
  currentTalentGuid: string;
  cdnRoot: string;
  companyName: string;
  presentationTypeName: string;
  presentationPublicData: any;
  className?: string;
  iconName: string;
  accountCountryCode?: COUNTRY;
  userName?: string;
  userNameModalToggle?: () => void;
  presentationGuid?: string;
  presentationId?: number;
  pickedBy?: { id: string; useName: number }[];
  pickCount?: number;
};

const TalentInfo = memo<TalentInfoProps>(
  ({
    isShareSelectsPresentation,
    roleId,
    folderId,
    currentTalentGuid,
    accountCountryCode,
    userName,
    userNameModalToggle,
    presentationId,
    presentationGuid,
    pickedBy,
    pickCount,
    showSummaryWithFolderCustomization,
  }) => (
    <ProfileViewWrapper>
      <ProfileView
        isPreview
        isRearrangeMedia
        isPublic
        showSummary
        roleId={roleId}
        folderId={folderId}
        showSummaryWithSubmissionDetails={isShareSelectsPresentation}
        showSummaryWithFolderCustomization={showSummaryWithFolderCustomization}
        hidePreviewButtons
        appType={AppType.Reps}
        guid={currentTalentGuid}
        accountCountryCode={accountCountryCode}
        userName={userName}
        userNameModalToggle={userNameModalToggle}
        presentationId={presentationId}
        presentationGuid={presentationGuid}
        pickedBy={pickedBy}
        pickCount={pickCount}
        showPicks
      >
        <ProfileView.TopNav />
        <ProfileView.Summary />
        <ProfileView.Media />
        <ProfileView.Credits />
        <ProfileView.Resume />
        <ProfileView.Skills />
        <ProfileView.OtherExperience />
        <ProfileView.Sizes />
      </ProfileView>
    </ProfileViewWrapper>
  )
);

const RecipientDetailViewMainContent: FC<RecipientDetailViewMainContentProps> = ({
  isShareSelectsPresentation,
  roleId,
  darkTheme,
  currentTalentGuid,
  cdnRoot,
  companyName,
  presentationTypeName,
  presentationPublicData,
  className,
  expired = false,
  iconName,
  accountCountryCode,
  userName,
  userNameModalToggle,
  presentationId,
  presentationGuid,
  pickedBy,
  pickCount,
  folderId,
  showSummaryWithFolderCustomization,
}) => (
  <Wrapper className={className}>
    <ImpactedServiceAlert />
    <StyledHeader
      darkTheme={darkTheme}
      displayBackToHomeLink={expired}
      presentationTypeName={presentationTypeName}
      presentationName={presentationPublicData.name}
      logo={presentationPublicData?.organization?.logo}
      companyName={presentationPublicData?.organization.name}
      parentCompanyName={presentationPublicData?.organization?.parentName}
      projectName={presentationPublicData?.project?.internalName || presentationPublicData?.project?.name}
    />
    {!expired ? (
      <>
        <TalentInfo
          isShareSelectsPresentation={isShareSelectsPresentation}
          showSummaryWithFolderCustomization={showSummaryWithFolderCustomization}
          roleId={roleId}
          folderId={folderId}
          currentTalentGuid={currentTalentGuid}
          accountCountryCode={accountCountryCode}
          userName={userName}
          userNameModalToggle={userNameModalToggle}
          presentationId={presentationId}
          presentationGuid={presentationGuid}
          pickedBy={pickedBy}
          pickCount={pickCount}
        />
      </>
    ) : (
      <RecipientViewExpired
        isExpired
        iconName={iconName}
        object="folder"
        typeName={presentationTypeName.toLowerCase()}
      />
    )}
    <StyledFooter cdnRoot={cdnRoot} companyName={companyName} />
  </Wrapper>
);

export default memo<RecipientDetailViewMainContentProps>(RecipientDetailViewMainContent);
